import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import api from './api';
import { useMobileOptimizations } from './hooks/useMobileOptimizations';
import { useIsMobile } from './hooks/useIsMobile';
import { hideSplashScreen } from './utils/splashScreen';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from 'capacitor-native-biometric';
import AnimatedSplash from './components/mobile/AnimatedSplash';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import CompanyDetails from './components/CompanyDetails';
import BranchDetails from './components/BranchDetails';
import BranchesPage from './components/BranchesPage';
import CompaniesPage from './components/CompaniesPage';
import { MobileLayout, ProfileMenu } from './components/mobile';
import OnboardingLayout from './components/OnboardingLayout';
import OnboardingOverview from './components/OnboardingOverview';
import OnboardingTasks from './components/OnboardingTasks';
import OnboardingSelectSystems from './components/OnboardingSelectSystems';
import OnboardingServerInfo from './components/OnboardingServerInfo';
import OnboardingWizard from './components/OnboardingWizard';
import UnifiedJourneyWizard from './components/journey-wizard';
import { SelectedCompanyProvider } from './contexts/SelectedCompanyContext';
import UsersPage from './components/UsersPage';
import OnboardingBranchList from './components/OnboardingBranchList';
import OnboardingBranchDetails from './components/OnboardingBranchDetails';
import ReportPage from './components/ReportPage';
import ServiceMonitorPage from './components/ServiceMonitorPage';
import AuditLogPage from './components/AuditLogPage';
import ManagementConsole from './components/settings/ManagementConsole';
import PlanningPage from './components/PlanningPage';
import DownloadsPage from './components/DownloadsPage';
import ToastProvider from './components/common/ToastProvider';
import { ThemeProvider } from './contexts/ThemeContext';
import ModernLayout from './components/layout/ModernLayout';
import SettingsPage from './components/SettingsPage';

// Journey Wizard Route wrapper component
function JourneyWizardRoute({ user }) {
  const navigate = useNavigate();
  
  const handleComplete = (data, action) => {
    switch (action) {
      case 'start-onboarding':
        navigate(`/onboarding/${data?.company?.id}`);
        break;
      case 'view-company':
        navigate(`/company/${data?.company?.id}`);
        break;
      case 'add-another':
        // Stay on journey page, component will reset
        window.location.reload();
        break;
      case 'view-dashboard':
      default:
        navigate('/');
        break;
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  return (
    <UnifiedJourneyWizard onComplete={handleComplete} onCancel={handleCancel} />
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Detect if running in mobile app (Capacitor)
  const isMobileApp = Capacitor.isNativePlatform();
  const isMobileWeb = useIsMobile();
  
  // Apply mobile optimizations
  useMobileOptimizations();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // For mobile apps, restore user immediately (so name persists on cold start)
        let restoredUser = null;
        if (isMobileApp) {
          const storedUserData = localStorage.getItem('userData');
          if (storedUserData) {
            try {
              restoredUser = JSON.parse(storedUserData);
              setUser(restoredUser);
              setIsLoggedIn(true);
            } catch (e) {
              console.error('📱 Failed to parse stored user data', e);
            }
          }
        }

        // Always fetch CSRF token (mobile stores it in localStorage; web uses cookie)
        const csrfResponse = await api.get('/auth/csrf').catch(() => null);
        if (isMobileApp && csrfResponse?.data?.csrfToken) {
          localStorage.setItem('csrfToken', csrfResponse.data.csrfToken);
        }

        // Mobile biometric gate (only if enabled and token exists)
        if (isMobileApp) {
          const biometricEnabled = localStorage.getItem('biometricEnabled') === 'true';
          const storedToken = localStorage.getItem('authToken');

          if (biometricEnabled && storedToken) {
            try {
              const result = await NativeBiometric.isAvailable();
              if (result.isAvailable) {
                const verified = await NativeBiometric.verifyIdentity({
                  reason: 'Unlock MiBOT Onboarding',
                  title: 'Face ID',
                  subtitle: 'Authenticate to continue',
                });
                if (!verified.verified) {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('csrfToken');
                  localStorage.removeItem('biometricEnabled');
                  localStorage.removeItem('userData');
                  setIsLoggedIn(false);
                  setUser(null);
                  return;
                }
              }
            } catch (error) {
              console.log('Biometric cancelled or failed:', error);
              localStorage.removeItem('authToken');
              localStorage.removeItem('csrfToken');
              localStorage.removeItem('biometricEnabled');
              localStorage.removeItem('userData');
              setIsLoggedIn(false);
              setUser(null);
              return;
            }
          }

          // If no token, skip verify (allow restored user only if present)
          if (!localStorage.getItem('authToken')) {
            if (!restoredUser) {
              setIsLoggedIn(false);
              setUser(null);
            }
            return;
          }
        }

        // Verify session
        const response = await api.get('/auth/verify');
        if (response.data.valid) {
          setIsLoggedIn(true);
          setUser(response.data.user);
          if (isMobileApp && response.data.user) {
            localStorage.setItem('userData', JSON.stringify(response.data.user));
          }
        } else {
          setIsLoggedIn(false);
          setUser(null);
          if (isMobileApp) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('csrfToken');
            localStorage.removeItem('userData');
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Only force logout if nothing is restored
        if (!isMobileApp || !localStorage.getItem('userData')) {
          setIsLoggedIn(false);
          setUser(null);
        }
      } finally {
        setIsLoading(false);
        hideSplashScreen();
      }
    };

    initializeApp();
  }, [isMobileApp]);

  const handleLogin = (userData) => {
    console.log('🔐 handleLogin called with userData:', userData);
    setIsLoggedIn(true);
    setUser(userData);
    
    // Store user data in localStorage for mobile apps
    if (isMobileApp && userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
      console.log('📱 User data stored in localStorage:', {
        name: userData.name,
        email: userData.email,
        role: userData.role
      });
      
      // Verify it was stored
      const stored = localStorage.getItem('userData');
      console.log('📱 Verification - userData in localStorage:', stored ? 'YES' : 'NO');
    }
  };

  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear HTTP-only cookie on server
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // For mobile apps, clear tokens and user data from localStorage
    if (isMobileApp) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('csrfToken');
      localStorage.removeItem('userData');
      console.log('📱 Auth, CSRF tokens, and user data cleared from mobile app');
    }
    
    setIsLoggedIn(false);
    setUser(null);
    // Clear all queries on logout
    queryClient.clear();
  };

  // Show loading state while verifying token
  if (isLoading) {
    return isMobileApp ? (
      <AnimatedSplash />
    ) : (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <SelectedCompanyProvider>
            <Router>
              <Routes>
                {!isLoggedIn && <Route path="*" element={<Login onLogin={handleLogin} />} />}
                {isLoggedIn && (
                  user.role === 'Client' ? (
                    isMobileApp ? (
                      // Mobile Client Routes
                      <Route element={<MobileLayout user={user} onLogout={handleLogout} />}>
                        <Route path="/reports" element={<ReportPage user={user} />} />
                        <Route path="/profile" element={<ProfileMenu user={user} onLogout={handleLogout} />} />
                        <Route path="*" element={<Navigate to="/reports" replace />} />
                      </Route>
                    ) : (
                      // Web Client Routes - Use Modern Layout
                      <Route element={<ModernLayout user={user} onLogout={handleLogout} />}>
                        <Route path="/reports" element={<ReportPage user={user} />} />
                        <Route path="*" element={<Navigate to="/reports" replace />} />
                      </Route>
                    )
                  ) : (
                    (isMobileApp || isMobileWeb) ? (
                      // Mobile Staff/Admin Routes
                      <Route element={<MobileLayout user={user} onLogout={handleLogout} />}>
                        <Route index element={<Dashboard user={user} onLogout={handleLogout} />} />
                        <Route path="journey" element={<JourneyWizardRoute user={user} />} />
                        <Route path="companies" element={<CompaniesPage user={user} />} />
                        <Route path="planning" element={<PlanningPage user={user} />} />
                        <Route path="downloads" element={<DownloadsPage />} />
                        <Route path="/company/:companyId" element={<CompanyDetails user={user} />} />
                        <Route path="/branch/:branchId" element={<BranchDetails user={user} />} />
                        <Route path="/branches" element={<BranchesPage user={user} />} />
                        {/* Onboarding nested routes */}
                        <Route path="/onboarding/:companyId" element={<OnboardingLayout />}>
                          <Route path="wizard" element={<OnboardingWizard />} />
                          <Route path="select-systems" element={<OnboardingSelectSystems />} />
                          <Route index element={<OnboardingOverview />} />
                          <Route path="setup" element={<Navigate to="../tasks" replace />} />
                          <Route path="tasks" element={<OnboardingTasks />} />
                          <Route path="server-info" element={<OnboardingServerInfo />} />
                          <Route path="branches" element={<OnboardingBranchList />} />
                          <Route path="branches/:branchId" element={<OnboardingBranchDetails user={user} />} />
                        </Route>
                        <Route path="/users" element={<UsersPage user={user} />} />
                        <Route path="/admin/services" element={<ServiceMonitorPage />} />
                        {user.role === 'Admin' && <Route path="/admin/audit-log" element={<AuditLogPage />} />}
                        {user.role === 'Admin' && <Route path="/admin/management-console" element={<ManagementConsole user={user} />} />}
                        <Route path="/reports" element={<ReportPage user={user} />} />
                        {/* Profile page for mobile */}
                        <Route path="/profile" element={<ProfileMenu user={user} onLogout={handleLogout} />} />
                      </Route>
                    ) : (
                      // Web Staff/Admin Routes - Use Modern Layout
                      <Route element={<ModernLayout user={user} onLogout={handleLogout} />}>
                        <Route index element={<Dashboard user={user} onLogout={handleLogout} />} />
                        <Route path="journey" element={<JourneyWizardRoute user={user} />} />
                        <Route path="companies" element={<CompaniesPage user={user} />} />
                        <Route path="planning" element={<PlanningPage user={user} />} />
                        <Route path="downloads" element={<DownloadsPage />} />
                        <Route path="settings" element={<SettingsPage user={user} />} />
                        <Route path="/company/:companyId" element={<CompanyDetails user={user} />} />
                        <Route path="/branch/:branchId" element={<BranchDetails user={user} />} />
                        <Route path="/branches" element={<BranchesPage user={user} />} />
                        {/* Onboarding nested routes */}
                        <Route path="/onboarding/:companyId" element={<OnboardingLayout />}>
                          <Route path="wizard" element={<OnboardingWizard />} />
                          <Route path="select-systems" element={<OnboardingSelectSystems />} />
                          <Route index element={<OnboardingOverview />} />
                          <Route path="setup" element={<Navigate to="../tasks" replace />} />
                          <Route path="tasks" element={<OnboardingTasks />} />
                          <Route path="server-info" element={<OnboardingServerInfo />} />
                          <Route path="branches" element={<OnboardingBranchList />} />
                          <Route path="branches/:branchId" element={<OnboardingBranchDetails user={user} />} />
                        </Route>
                        <Route path="/users" element={<UsersPage user={user} />} />
                        <Route path="/admin/services" element={<ServiceMonitorPage />} />
                        {user.role === 'Admin' && <Route path="/admin/audit-log" element={<AuditLogPage />} />}
                        {user.role === 'Admin' && <Route path="/admin/management-console" element={<ManagementConsole user={user} />} />}
                        {/* Staff and customer report */}
                        <Route path="/reports" element={<ReportPage user={user} />} />
                      </Route>
                    )
                  )
                )}
              </Routes>
            </Router>
          </SelectedCompanyProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
